import { Center, LoadingOverlay, Pagination, Paper, Box, TextInput } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { Lead, Campaign } from '@ospace/schemas'
import { ColDef, DashboardDetailedModal, NewTable } from '@ospace/shared'
import { useMemo, useState } from 'react'

import { TableHeader } from './TableHeader'
import { transformModalData } from './transformModalData'
import { IconSearch } from '@tabler/icons-react'

type LeadsTableProps = {
  leads: Lead[]
  currency: string
  campaigns: Campaign[]
  page?: number
  totalPages?: number
  onPageChange?: (page: number) => void
  loading?: boolean
  search?: string
  onSearchChange?: (value: string) => void
}

export const LeadsTable = ({
  leads,
  currency,
  campaigns,
  page,
  totalPages,
  onPageChange,
  loading,
  search,
  onSearchChange,
}: LeadsTableProps) => {
  const leadsWithCampaignName = useMemo(() => {
    const campaignMap = new Map(campaigns.map((camp) => [camp.id, camp.name]))

    return leads.map((lead: Lead) => ({
      ...lead,
      campaignName: lead.campaignId != null ? campaignMap.get(lead.campaignId) : undefined,
    }))
  }, [leads, campaigns])

  const colDef: ColDef[] = [
    {
      key: 'companyName',
      label: 'Company Name',
    },
    {
      key: 'companyDomain',
      label: 'Website',
    },
    {
      key: 'companyCity',
      label: 'City',
    },
    {
      key: 'companyNumberofEmployees',
      label: 'Employees',
    },
    {
      key: 'companyIndustry',
      label: 'Industry',
    },
    {
      key: 'companyAnnualRevenue',
      label: 'Revenue',
      dataType: 'currency',
      currency,
    },
    {
      key: 'campaignName',
      label: 'Program',
    },
    {
      key: 'status',
      label: 'Status',
    },
  ]

  const [opened, { open, close }] = useDisclosure(false)

  const [modalData, setModalData] = useState<Lead & { campaignName: string }>()
  const serverSide = page !== undefined && totalPages !== undefined && onPageChange !== undefined

  return (
    <Paper withBorder p='xl' radius='md'>
      <TableHeader />
      {serverSide && (
        <Center mt='xl'>
          <TextInput
            placeholder='Search companies...'
            value={search}
            onChange={(e) => onSearchChange?.(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            w={300}
          />
        </Center>
      )}

      <Box pos='relative'>
        <LoadingOverlay
          visible={!!loading}
          zIndex={10}
          overlayProps={{
            blur: 1,
          }}
        />

        <Box
          style={{
            opacity: loading ? 0.6 : 1,
            transition: 'opacity 150ms ease',
          }}
        >
          <NewTable
            colDef={colDef}
            tableData={leadsWithCampaignName}
            tableProps={{
              itemsPerPage: 15,
              rowKey: 'id',
            }}
            hidePagination={true}
            hideSearch={true}
            modal={{
              component: (
                <DashboardDetailedModal
                  opened={opened}
                  onClose={close}
                  centered
                  data={transformModalData(modalData)}
                  colDef={colDef}
                />
              ),
              setModalData,
              open,
            }}
          />
        </Box>
      </Box>

      {serverSide && (
        <Center mt='xl'>
          <Pagination value={page} onChange={onPageChange} total={totalPages} disabled={loading} />
        </Center>
      )}
    </Paper>
  )
}
